'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useTranslation } from '@/hooks/useTranslation';
import { aiUsageApi, SummaryResponse, BreakdownRow, DeviceRow, BlockRow } from '../_lib/api';

export default function MyAiUsagePage() {
  const { t } = useTranslation();
  const tt = t.aiUsage;
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [projects, setProjects] = useState<BreakdownRow[]>([]);
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [blocks, setBlocks] = useState<BlockRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      aiUsageApi.mySummary({ period: 'month' }),
      aiUsageApi.myBreakdown({ period: 'month', by: 'project', page: 1, pageSize: 10 }),
      aiUsageApi.myDevices(),
      aiUsageApi.myBlocks(),
    ])
      .then(([s, b, d, bl]) => {
        setSummary(s);
        setProjects(b.items);
        setDevices(d.items);
        setBlocks(bl.blocks);
      })
      .finally(() => setLoading(false));
  }, []);

  const activeBlock = blocks.find((b) => b.isActive);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">{tt.nav.me}</h1>
        <Link href="/ai-usage/me/tokens" className="px-3 py-2 bg-blue-600 text-white text-sm rounded">
          {tt.nav.myTokens}
        </Link>
      </div>

      {loading && <div className="text-gray-500 text-sm">Loading…</div>}

      {!loading && summary && (
        <>
          {summary.activeDevices === 0 && (
            <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded p-4 text-sm">
              {tt.empty.noOwnToken}{' '}
              <Link href="/ai-usage/me/tokens" className="text-blue-600 hover:underline ml-2">
                {tt.empty.createFirst} →
              </Link>
            </div>
          )}

          {activeBlock && (
            <div className="bg-blue-50 border border-blue-200 rounded shadow p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-blue-700 font-medium">{tt.block.activeTitle}</div>
                  <div className="text-2xl font-semibold mt-1">
                    ${activeBlock.costUsd} <span className="text-sm text-gray-500">/ 5h</span>
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {tt.block.elapsed}: {activeBlock.elapsedHours}h ·{' '}
                    {tt.block.remaining}: {activeBlock.remainingHours}h ·{' '}
                    {tt.block.burnRate}: ${activeBlock.burnRateUsdPerHour}/h
                  </div>
                </div>
                <div className="text-right text-xs text-gray-500">
                  <div>{activeBlock.events} events</div>
                  <div>{(activeBlock.tokens / 1e6).toFixed(2)}M tok</div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded shadow p-4">
              <div className="text-xs text-gray-500">{tt.kpi.totalCost}</div>
              <div className="text-2xl font-semibold">${summary.totalCostUsd}</div>
            </div>
            <div className="bg-white rounded shadow p-4">
              <div className="text-xs text-gray-500">{tt.kpi.totalTokens}</div>
              <div className="text-2xl font-semibold">{summary.totalTokens.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded shadow p-4">
              <div className="text-xs text-gray-500">{tt.kpi.activeDevices}</div>
              <div className="text-2xl font-semibold">{summary.activeDevices}</div>
            </div>
          </div>

          <div className="bg-white rounded shadow p-4 mb-6">
            <h2 className="font-semibold mb-3">{tt.breakdown.tab.project}</h2>
            <table className="w-full text-sm">
              <thead className="text-gray-500 border-b">
                <tr>
                  <th className="py-2 text-left">{tt.breakdown.column.name}</th>
                  <th className="py-2 text-right">{tt.breakdown.column.tokens}</th>
                  <th className="py-2 text-right">{tt.breakdown.column.cost}</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.key} className="border-b">
                    <td className="py-2 font-mono text-xs">{p.key}</td>
                    <td className="py-2 text-right">{p.tokens.toLocaleString()}</td>
                    <td className="py-2 text-right">${p.costUsd}</td>
                  </tr>
                ))}
                {projects.length === 0 && (
                  <tr>
                    <td colSpan={3} className="py-8 text-center text-gray-400">
                      {tt.empty.waitingFirstEvent}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded shadow p-4 mb-6">
            <h2 className="font-semibold mb-3">{tt.device.listTitle}</h2>
            <table className="w-full text-sm">
              <thead className="text-gray-500 border-b">
                <tr>
                  <th className="py-2 text-left">{tt.device.columnHostname}</th>
                  <th className="py-2 text-left">{tt.device.columnPlatform}</th>
                  <th className="py-2 text-left">{tt.device.columnLastSeen}</th>
                  <th className="py-2 text-left">{tt.device.columnStatus}</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.id} className="border-b">
                    <td className="py-2 font-mono text-xs">{d.hostname}</td>
                    <td className="py-2">{d.osPlatform.toLowerCase()}</td>
                    <td className="py-2 text-gray-500">{new Date(d.lastSeenAt).toLocaleString()}</td>
                    <td className="py-2">
                      {d.blockedAt ? (
                        <span className="text-red-600">{tt.device.statusBlocked}</span>
                      ) : (
                        <span className="text-green-600">{tt.device.statusActive}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {blocks.length > 0 && (
            <div className="bg-white rounded shadow p-4 mb-6">
              <h2 className="font-semibold mb-3">{tt.block.recentTitle}</h2>
              <table className="w-full text-sm">
                <thead className="text-gray-500 border-b">
                  <tr>
                    <th className="py-2 text-left">{tt.block.colStarted}</th>
                    <th className="py-2 text-left">{tt.block.colDuration}</th>
                    <th className="py-2 text-right">{tt.block.colEvents}</th>
                    <th className="py-2 text-right">{tt.block.colTokens}</th>
                    <th className="py-2 text-right">{tt.block.colCost}</th>
                  </tr>
                </thead>
                <tbody>
                  {blocks.map((b) => (
                    <tr key={b.startedAt} className={`border-b ${b.isActive ? 'bg-blue-50' : ''}`}>
                      <td className="py-2 text-xs text-gray-500">{new Date(b.startedAt).toLocaleString()}</td>
                      <td className="py-2 text-xs">
                        {b.isActive ? `${b.elapsedHours}h / 5h ${tt.block.active}` : `5h`}
                      </td>
                      <td className="py-2 text-right">{b.events}</td>
                      <td className="py-2 text-right">{(b.tokens / 1e6).toFixed(2)}M</td>
                      <td className="py-2 text-right">${b.costUsd}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <details className="bg-white rounded shadow p-4 text-sm">
            <summary className="cursor-pointer font-semibold">{tt.privacy.faqTitle}</summary>
            <div className="mt-3 text-gray-700 space-y-2">
              <div>✅ {tt.privacy.whatCollected}</div>
              <div>🚫 {tt.privacy.whatNotCollected}</div>
            </div>
          </details>
        </>
      )}
    </div>
  );
}
